// simulator/utils/agendaLlenaDemo.js
// Datos de ejemplo de «Tu agenda llena» para el simulador de ventas (citas). Puro y determinista: sin
// fetch, sin axios y sin Math.random; todo relativo a la fecha que se le pase (hoy en Madrid).
//
// Forma = la que sirve c_clientInsightsApi (op resumen / lista), que es la que lee la pantalla real:
//   resumen: { por_tarjeta, por_trabajador, ticket_medio, huecos, dias_de_datos, clientes_total, calculado_at }
//   cliente: { phone, nombre, ultima, ciclo_dias?, servicio, trabajador, aviso_at? }
//   hueco:   { day, franja: "manana" | "tarde", worker, libres, candidatos: [phone] }
//
// Cifras de la demo: les toca volver 120, vinieron una vez 134, se retrasan 22 y perdidos 8 (30 en total),
// unos 15 huecos esta semana, ticket medio 12,76 € y 18 vueltas gracias a los avisos este mes (230 €, de 120 enviados).
// Embudo de «Resultados de tus avisos» (mes actual): 120 enviados -> 114 entregados -> 86 leídos -> 31 abrieron ->
// 18 reservaron · 230 € por 7,20 € de avisos; los dos meses anteriores, algo menos.
//
// Sector (28/10/2026): trabajadores y servicios son los del sector del simulador, y el ticket medio sale de los
// precios de SUS servicios (mediana): un tatuador no cobra 12,76 €. Los euros del embudo se escalan con ese ticket
// (con 12,76 € quedan las cifras de arriba). `resumen.donde_ayuda` se calcula con la MISMA regla que el cálculo
// nocturno (utils/dondeAyuda.js, copia del lambda) y `explicaAgenda` saca de estos mismos datos los ejemplos de la
// cabecera «Qué detecta la IA» del simulador: nada de cifras sueltas que contradigan las tarjetas.
import { dondeAyuda } from "./dondeAyuda";

export const DEMO_COUNTS = { volver: 120, nuevo: 134, retrasado: 22, perdido: 8 };
export const DEMO_HUECOS = 15;
export const DEMO_TICKET_MEDIO = 12.76;
export const DEMO_VUELTAS = { sent: 120, returned: 18 }; // = el total del mes actual del embudo de ejemplo

// Embudo de ejemplo por mes (0 = mes actual, 1 = el anterior, 2 = el otro). Por tarjeta:
// [enviados, entregados, leidos, abrieron, reservaron, ingresos_eur]; fallidos = enviados - entregados.
// Los totales del mes son la suma de sus tarjetas (el test lo vigila) y el coste, enviados x 0,06 €.
const EMBUDO_DEMO = [
  { volver: [70, 67, 52, 19, 11, 140], nuevo: [34, 32, 23, 8, 4, 55], perdido: [16, 15, 11, 4, 3, 35] },
  { volver: [40, 38, 28, 10, 6, 70], nuevo: [16, 15, 10, 3, 2, 25], perdido: [8, 8, 6, 2, 1, 17] },
  { volver: [18, 17, 12, 4, 2, 30], nuevo: [8, 8, 5, 1, 1, 12], perdido: [4, 4, 3, 1, 0, 6] },
];
const redondea = (n) => Math.round(n * 100) / 100;

/** Mes `YYYY-MM` de `hoy` (YYYY-MM-DD) desplazado `atras` meses. */
const mesAtras = (hoy, atras) => {
  const [y, m] = String(hoy).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1 - atras, 1)).toISOString().slice(0, 7);
};

/** Forma de #RESUMEN.embudo (ver c_clientInsightsNightly/embudoRules.agregarEmbudo) con las cifras de ejemplo. */
export function embudoDemo(hoy, ticket = DEMO_TICKET_MEDIO) {
  // Los euros que dejaron las reservas, a la escala del ticket del sector (con el de la demo, las cifras de arriba).
  const escala = Number(ticket) > 0 ? Number(ticket) / DEMO_TICKET_MEDIO : 1;
  const out = {};
  EMBUDO_DEMO.forEach((tarjetas, i) => {
    const total = { enviados: 0, entregados: 0, leidos: 0, fallidos: 0, abrieron: 0, reservaron: 0, ingresos_eur: 0, importes_estimados: 0, coste_eur: 0 };
    const mes = {};
    for (const [tarjeta, [env, ent, lei, abr, res, ing]] of Object.entries(tarjetas)) {
      mes[tarjeta] = { enviados: env, entregados: ent, leidos: lei, fallidos: env - ent, abrieron: abr, reservaron: res, ingresos_eur: Math.round(ing * escala), importes_estimados: 0, coste_eur: redondea(env * 0.06) };
      for (const k of ["enviados", "entregados", "leidos", "fallidos", "abrieron", "reservaron", "ingresos_eur"]) total[k] += mes[tarjeta][k];
    }
    total.coste_eur = redondea(total.enviados * 0.06);
    out[mesAtras(hoy, i)] = { ...mes, total };
  });
  return out;
}

const NOMBRES = [
  "María", "Lucía", "Carmen", "Laura", "Marta", "Elena", "Paula", "Sara", "Andrea", "Nuria", "Irene",
  "Ana", "Alba", "Sofía", "Claudia", "Raquel", "Pilar", "Inés", "Rosa", "Eva", "Javier", "Carlos",
  "Pablo", "David", "Sergio", "Álvaro", "Miguel", "Adrián", "Rubén", "Hugo", "Jorge",
]; // 31
const APELLIDOS = [
  "García", "Martínez", "López", "Sánchez", "Pérez", "Gómez", "Fernández", "Ruiz", "Díaz", "Moreno",
  "Muñoz", "Álvarez", "Romero", "Navarro", "Torres", "Domínguez", "Gil", "Vázquez", "Serrano", "Ramos",
  "Blanco", "Molina", "Castro", "Ortega", "Rubio", "Marín", "Iglesias", "Medina", "Cortés",
]; // 29 — 31 y 29 son primos entre sí: (i % 31, i % 29) no se repite por debajo de 899 clientes
const SERVICIOS_DEFECTO = ["Corte", "Corte y lavado", "Tinte", "Manicura", "Limpieza facial"];
const TRABAJADORES_DEFECTO = [
  { id: "w1", name: "Laura Sánchez" },
  { id: "w2", name: "Carlos Ruiz" },
];

const DAY_MS = 24 * 3600 * 1000;
const ymd = (ms) => new Date(ms).toISOString().slice(0, 10);
const utcDia = (hoy) => {
  const [y, m, d] = String(hoy).split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};
/** `hoy` (YYYY-MM-DD) menos `dias`. */
export const diaAtras = (hoy, dias) => ymd(utcDia(hoy) - dias * DAY_MS);
/** `hoy` (YYYY-MM-DD) más `dias`. */
export const diaMas = (hoy, dias) => ymd(utcDia(hoy) + dias * DAY_MS);

/**
 * Ticket medio de la demo a partir de los servicios del sector: la mediana de los precios mayores que 0 (una
 * consulta gratis no es un ticket). Sin precios, el de siempre (12,76 €).
 */
export function ticketDemo(services) {
  const precios = (services || []).map((s) => Number(s && s.price)).filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  if (!precios.length) return DEMO_TICKET_MEDIO;
  const m = Math.floor(precios.length / 2);
  return redondea(precios.length % 2 ? precios[m] : (precios[m - 1] + precios[m]) / 2);
}

/** Trabajadores del simulador → [{ id, name }] (máximo 3); sin ninguno, dos de ejemplo. */
export function workersDemo(workers) {
  const lista = (workers || [])
    .filter((w) => w && w.active !== false && (w.name || w.full_name))
    .slice(0, 3)
    .map((w, i) => ({ id: String(w.id || w.worker_id || w.user_id || `w${i + 1}`), name: w.name || w.full_name }));
  return lista.length ? lista : TRABAJADORES_DEFECTO;
}

/**
 * Reparte `n` clientes de una tarjeta. `diasDe(k)` da los días desde la última visita del k-ésimo y
 * `cicloDe(k)` su ciclo (null = una sola visita).
 */
function clientes(tarjeta, n, offset, { hoy, workers, servicios, diasDe, cicloDe }) {
  const out = [];
  for (let k = 0; k < n; k += 1) {
    const i = offset + k;
    const ciclo = cicloDe(k);
    out.push({
      phone: `+346${String(10000000 + 1000 + i).padStart(8, "0")}`, // +34 6 + 8 dígitos = móvil de 9 cifras
      nombre: `${NOMBRES[i % NOMBRES.length]} ${APELLIDOS[i % APELLIDOS.length]}`,
      ultima: diaAtras(hoy, diasDe(k, ciclo)),
      ...(ciclo ? { ciclo_dias: ciclo } : {}),
      servicio: servicios[(i * 3 + k) % servicios.length],
      trabajador: workers[i % workers.length].id,
      tarjeta,
    });
  }
  return out;
}

const CICLOS = [21, 28, 28, 35, 42, 30, 45, 56, 60, 25];

/**
 * Todo el ejemplo: listas por tarjeta, resumen y huecos.
 * @param {{ workers?: object[], services?: object[], hoy: string }} p  `hoy` = YYYY-MM-DD en Madrid.
 */
export function buildAgendaLlenaDemo({ workers, services, hoy }) {
  const ws = workersDemo(workers);
  const servicios = (services || []).map((s) => s && (s.name || s.nombre)).filter(Boolean).slice(0, 6);
  const sv = servicios.length ? servicios : SERVICIOS_DEFECTO;
  const ctx = { hoy, workers: ws, servicios: sv };

  // volver: días desde la última ∈ [ciclo, ciclo + 14].
  const volver = clientes("volver", DEMO_COUNTS.volver, 0, {
    ...ctx, cicloDe: (k) => CICLOS[k % CICLOS.length], diasDe: (k, c) => c + (k % 15),
  });
  // nuevo: una visita, entre 30 y 90 días.
  const nuevo = clientes("nuevo", DEMO_COUNTS.nuevo, DEMO_COUNTS.volver, {
    ...ctx, cicloDe: () => null, diasDe: (k) => 30 + ((k * 7) % 61),
  });
  // retrasado: entre 1,5 y 2 veces su ciclo.
  const retrasado = clientes("retrasado", DEMO_COUNTS.retrasado, DEMO_COUNTS.volver + DEMO_COUNTS.nuevo, {
    ...ctx, cicloDe: (k) => CICLOS[(k + 3) % CICLOS.length],
    diasDe: (k, c) => Math.round(c * (1.5 + ((k % 6) * 0.08))),
  });
  // perdido: más del doble de su ciclo, o una visita hace más de 90 días.
  const perdido = clientes("perdido", DEMO_COUNTS.perdido, DEMO_COUNTS.volver + DEMO_COUNTS.nuevo + DEMO_COUNTS.retrasado, {
    ...ctx, cicloDe: (k) => (k % 2 === 0 ? CICLOS[(k + 5) % CICLOS.length] : null),
    diasDe: (k, c) => (c ? Math.round(c * 2.4) + k * 3 : 100 + k * 9),
  });

  const listas = { volver, nuevo, retrasado, perdido };

  const cuenta = (filtro) => ({
    volver: volver.filter(filtro).length,
    nuevo: nuevo.filter(filtro).length,
    retrasado: retrasado.filter(filtro).length,
    perdido: perdido.filter(filtro).length,
  });
  const por_trabajador = {};
  for (const w of ws) por_trabajador[w.id] = cuenta((c) => c.trabajador === w.id);

  // Huecos de los próximos 7 días en franjas que no se llenan solas: 15, repartidos entre días, franjas y
  // trabajadores. Los candidatos son clientes de «Les toca volver» (el aviso les ofrece ese hueco).
  const huecos = [];
  const franjas = ["tarde", "manana", "tarde", "tarde", "manana"];
  for (let k = 0; k < DEMO_HUECOS; k += 1) {
    const day = diaMas(hoy, 1 + (k % 7));
    const franja = franjas[k % franjas.length];
    const worker = ws[k % ws.length].id;
    const n = [8, 5, 7, 3, 9, 2, 6, 4, 0, 5, 3, 7, 2, 4, 6][k];
    const candidatos = [];
    for (let j = 0; j < n; j += 1) candidatos.push(volver[(k * 11 + j * 5) % volver.length].phone);
    huecos.push({ day, franja, worker, libres: 1 + (k % 3), candidatos });
  }

  const ticket = ticketDemo(services);
  const resumen = {
    por_tarjeta: { volver: volver.length, nuevo: nuevo.length, retrasado: retrasado.length, perdido: perdido.length },
    por_trabajador,
    ticket_medio: ticket,
    huecos,
    dias_de_datos: 240,
    clientes_total: volver.length + nuevo.length + retrasado.length + perdido.length + 96,
    calculado_at: `${hoy}T04:30:00.000Z`,
  };
  // «Dónde te ayuda»: la misma regla que c_clientInsightsNightly (sin la lista de clientes: con estas cifras nunca
  // sale «al día», que es lo único que la usa).
  resumen.donde_ayuda = dondeAyuda({ summary: resumen, clients: [] });

  return { listas, resumen, workers: ws, vueltas_mes: { ...DEMO_VUELTAS }, embudo: embudoDemo(hoy, ticket) };
}

// Huecos que caben en una franja (mañana o tarde) de UN trabajador: la base de la ocupación de la rejilla.
export const CAPACIDAD_FRANJA = 4;
const FRANJAS = ["manana", "tarde"];
const diasEntre = (desde, hasta) => Math.round((utcDia(hasta) - utcDia(desde)) / DAY_MS);

/**
 * Lo que enseña la cabecera «Qué detecta la IA» del simulador, sacado de los MISMOS datos que las tarjetas (y con
 * el mismo filtro de trabajador), para que ninguna cifra la contradiga:
 *   ritmo:     clientes con su ritmo calculado + uno de ejemplo con sus últimas visitas (el más pasado de su ritmo).
 *   nuevo:     los que vinieron una vez (= tarjeta «Vinieron una vez») + uno de ejemplo.
 *   perdido:   retrasados + perdidos (= tarjeta «Se retrasan o los has perdido») + uno de ejemplo con su ritmo.
 *   ocupacion: los próximos 7 días × mañana/tarde con los huecos libres de `resumen.huecos`, la ocupación y la
 *              franja con más huecos, con el hueco concreto al que avisar (el de más candidatos).
 */
export function explicaAgenda({ listas, resumen, hoy, trabajador = "", nWorkers = 1 }) {
  const de = (l) => (l || []).filter((c) => !trabajador || c.trabajador === trabajador);
  const volver = de(listas && listas.volver);
  const nuevo = de(listas && listas.nuevo);
  const retrasado = de(listas && listas.retrasado);
  const perdido = de(listas && listas.perdido);
  const dias = (c) => diasEntre(c.ultima, hoy);
  const masPasado = (l, f) => l.reduce((best, c) => (best === null || f(c) > f(best) ? c : best), null);

  const conRitmo = [...volver, ...retrasado, ...perdido].filter((c) => c.ciclo_dias);
  const ej = masPasado(volver.filter((c) => c.ciclo_dias), (c) => dias(c) - c.ciclo_dias);
  const ritmo = {
    count: conRitmo.length,
    ejemplo: ej ? {
      nombre: ej.nombre, ciclo: ej.ciclo_dias, dias: dias(ej), servicio: ej.servicio,
      // Sus cuatro últimas visitas, a su ritmo: la última es la de la ficha.
      visitas: [3, 2, 1, 0].map((n) => diaAtras(ej.ultima, n * ej.ciclo_dias)),
    } : null,
  };

  const ejNuevo = masPasado(nuevo, dias);
  const ejPerdido = masPasado(retrasado.filter((c) => c.ciclo_dias), (c) => dias(c) / c.ciclo_dias)
    || masPasado(perdido.filter((c) => c.ciclo_dias), (c) => dias(c) / c.ciclo_dias);

  const huecos = ((resumen && resumen.huecos) || []).filter((h) => !trabajador || h.worker === trabajador);
  const capacidad = CAPACIDAD_FRANJA * (trabajador ? 1 : Math.max(1, nWorkers));
  const diasSemana = [];
  let mejor = null;
  for (let i = 1; i <= 7; i += 1) {
    const day = diaMas(hoy, i);
    const franjas = {};
    for (const franja of FRANJAS) {
      const suyos = huecos.filter((h) => h.day === day && h.franja === franja);
      const libres = Math.min(capacidad, suyos.reduce((a, h) => a + (Number(h.libres) || 0), 0));
      const candidatos = new Set(suyos.flatMap((h) => h.candidatos || [])).size;
      const pct = Math.round((100 * (capacidad - libres)) / capacidad);
      franjas[franja] = { libres, candidatos, pct };
      // El hueco concreto al que avisar desde la cabecera: el de esa franja con más candidatos. Sus candidatos son los
      // que cuenta el texto, los mismos a los que escribirá el modal de envío.
      const avisar = [...suyos].sort((a, b) => (b.candidatos || []).length - (a.candidatos || []).length)[0];
      const nAvisar = avisar ? (avisar.candidatos || []).length : 0;
      if (libres > 0 && (!mejor || libres > mejor.libres || (libres === mejor.libres && nAvisar > mejor.candidatos))) {
        mejor = { day, franja, libres, candidatos: nAvisar, hueco: nAvisar ? { day, franja, worker: avisar.worker } : null };
      }
    }
    diasSemana.push({ day, franjas });
  }

  return {
    ritmo,
    nuevo: { count: nuevo.length, ejemplo: ejNuevo ? { nombre: ejNuevo.nombre, dias: dias(ejNuevo), servicio: ejNuevo.servicio } : null },
    perdido: { count: retrasado.length + perdido.length, ejemplo: ejPerdido ? { nombre: ejPerdido.nombre, ciclo: ejPerdido.ciclo_dias, dias: dias(ejPerdido) } : null },
    ocupacion: { dias: diasSemana, mejor, capacidad },
  };
}
